import React from 'react';
import ProductListSort from './product-list-sort';
import ColumnToggle from './column-toggle';
import PriceSection from './price-section';
import CategoriesSection from './categories-section';
import SizesSection from './sizes-section';
import ColorSection from './color-section';
import SortSection from './sort-section';
import FilterBullets from './filter-bullets';
import FilterToolbar from './filter-toolbar';
import useProductListFilters from '../../hooks/useProductListFilters';
import { CusProductListProductListFiltersProps } from '../../cus-product-list.data';
import { parseFilterOptions } from '../../../../utils/productListFilters';

const DRAWER_ID = 'bs-product-list-filter-drawer';
const EMPTY_FILTER_OBJECT = { priceRange: { minPrice: 0, maxPrice: 0 }, categories: [], sizes: [], colorGroups: [] };

export default function ProductListFilters({
    props,
    currentSort,
    onSortChange,
    mobileColumns,
    onMobileColumnsChange,
    onSearch,
    isLoading
}: CusProductListProductListFiltersProps) {
    const filters = props.data.filters.result;
    const initialFilters = parseFilterOptions(k => props.context.request.query?.[k]) ?? {};
    const currency = props.context.request.channel?.Currency ?? '';
    const {
        filterText,
        modalTitle,
        modalCloseButtonText,
        clearFiltersText,
        clearAllFiltersText,
        closeFiltersAriaLabel,
        priceFilterHeading,
        minimumPriceAriaLabel,
        maximumPriceAriaLabel,
        sizesFilterHeading,
        categoriesFilterHeading,
        colorsFilterHeading
    } = props.resources;

    const {
        toggle,
        close,
        activeFilters,
        draftFilters,
        setDraftFilters,
        activeCount,
        toolbarSentinelRef,
        handleClearAll,
        handleRemoveBullet,
        categoryNames,
        handleSearch,
        isOpen,
        showFloatingBar
    } = useProductListFilters({
        onSearch,
        filters: filters ?? EMPTY_FILTER_OBJECT,
        initialFilters
    });

    if (!filters) {
        return null;
    }

    return (
        <>
            <div className='bs-product-list__toolbar-row'>
                <FilterToolbar
                    ref={toolbarSentinelRef}
                    isOpen={isOpen}
                    activeCount={activeCount}
                    filterText={filterText}
                    clearFiltersText={clearFiltersText}
                    drawerId={DRAWER_ID}
                    onToggle={toggle}
                    onClearAll={handleClearAll}
                    ariaHidden={showFloatingBar}
                />
                <div className='bs-product-list__toolbar-row__sort-slot'>
                    <ProductListSort props={props} currentSort={currentSort} onSortChange={onSortChange} />
                    <ColumnToggle
                        singleColumnViewAriaLabel={props.resources.singleColumnViewAriaLabel}
                        twoColumnViewAriaLabel={props.resources.twoColumnViewAriaLabel}
                        columns={mobileColumns}
                        onChange={onMobileColumnsChange}
                    />
                </div>
            </div>
            {showFloatingBar && (
                <FilterToolbar
                    variant='floating'
                    isOpen={isOpen}
                    activeCount={activeCount}
                    filterText={filterText}
                    clearFiltersText={clearFiltersText}
                    drawerId={DRAWER_ID}
                    onToggle={toggle}
                    onClearAll={() => {
                        handleClearAll();
                        toolbarSentinelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                />
            )}
            <div className='bs-product-list__filter-area'>
                <div id={DRAWER_ID} aria-hidden={!isOpen} className={`bs-product-list-filters${isOpen ? ' --open' : ''}`}>
                    {isOpen && <div className='bs-product-list-filters__dismiss-overlay' onClick={close} aria-hidden='true' />}
                    <div className='bs-product-list-filters__drawer'>
                        <div className='bs-product-list-filters__drawer__header'>
                            <h2 className='bs-product-list-filters__drawer__header__title'>{modalTitle}</h2>
                            <button
                                type='button'
                                className='bs-product-list-filters__drawer__close-btn'
                                onClick={close}
                                aria-label={closeFiltersAriaLabel}
                            >
                                <span aria-hidden='true' />
                            </button>
                        </div>
                        <FilterBullets
                            activeFilters={activeFilters}
                            currency={currency}
                            categoryNames={categoryNames}
                            onRemove={handleRemoveBullet}
                            onClearAll={handleClearAll}
                            clearAllFiltersText={clearAllFiltersText}
                        />
                        <div className='bs-product-list-filters__content'>
                            <SortSection props={props} currentSort={currentSort} onSortChange={onSortChange} />
                            <PriceSection
                                minPrice={filters.priceRange.minPrice}
                                maxPrice={filters.priceRange.maxPrice}
                                currency={currency}
                                priceFilterHeading={priceFilterHeading}
                                minimumPriceAriaLabel={minimumPriceAriaLabel}
                                maximumPriceAriaLabel={maximumPriceAriaLabel}
                                selectedRange={draftFilters.priceRange ?? null}
                                onChange={(min, max) => {
                                    setDraftFilters(prev => ({ ...prev, priceRange: { min, max } }));
                                }}
                            />
                            <CategoriesSection
                                categories={filters.categories}
                                selectedIds={draftFilters.categoryIds}
                                categoriesFilterHeading={categoriesFilterHeading}
                                onSelectionChange={ids => {
                                    setDraftFilters(prev => ({ ...prev, categoryIds: ids }));
                                }}
                            />
                            <SizesSection
                                sizes={filters.sizes}
                                selectedValues={draftFilters.sizes}
                                sizesFilterHeading={sizesFilterHeading}
                                onSelectionChange={selected => {
                                    setDraftFilters(prev => ({ ...prev, sizes: selected }));
                                }}
                            />
                            <ColorSection
                                colors={filters.colorGroups}
                                selectedValues={draftFilters.colors}
                                colorsFilterHeading={colorsFilterHeading}
                                onSelectionChange={selected => {
                                    setDraftFilters(prev => ({ ...prev, colors: selected }));
                                }}
                            />
                        </div>
                        <div className='bs-product-list-filters__drawer__footer'>
                            <button
                                type='button'
                                className='bs-product-list-filters__drawer__footer__search-btn'
                                onClick={handleSearch}
                                disabled={isLoading}
                            >
                                {modalCloseButtonText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
